import React, { useEffect, useState } from 'react';
import { Save, Bell, Mail, AlertCircle } from 'lucide-react';
import { $api } from '../../api';
import ErrorMessage from '../ErrorMessage';
import SuccessMessage from '../SuccessMessage';

const NotificationSettings: React.FC = () => {
  const [emailAlertsEnabled, setEmailAlertsEnabled] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);

  // Fetch current notification settings
  const notificationsQuery = $api.useQuery('get', '/api/settings/notifications')

  useEffect(() => {
    if(notificationsQuery.data) {
      setEmailAlertsEnabled(notificationsQuery.data.email_alerts_enabled);
    }
  }, [notificationsQuery.data]);

  // Update notification settings mutation
  const updateNotificationsMutation = $api.useMutation('put', '/api/settings/notifications');

  const handleToggle = (enabled: boolean) => {
    setEmailAlertsEnabled(enabled);
    setHasChanges(true);
    setSuccess(null);
  };

  const handleSave = () => {
    updateNotificationsMutation.mutate(
      {
        body: {
          email_alerts_enabled: emailAlertsEnabled,
        },
      },
      {
        onSuccess: () => {
          setSuccess('Préférences de notification enregistrées avec succès');
          setHasChanges(false);
          notificationsQuery.refetch();
          setTimeout(() => setSuccess(null), 5000);
        },
      }
    );
  };

  const isLoading = notificationsQuery.isLoading;
  const isSaving = updateNotificationsMutation.isPending;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header */}
      <div className="border-b border-gray-200 p-6">
        <div className="flex items-center space-x-2">
          <Bell className="h-5 w-5 text-gray-600" />
          <h3 className="text-lg font-semibold text-gray-900">Préférences de Notification</h3>
        </div>
        <p className="text-sm text-gray-600 mt-1">
          Gérez vos préférences pour recevoir des notifications par email
        </p>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        )}

        {/* Error State */}
        {notificationsQuery.error && (
          <ErrorMessage error={notificationsQuery.error} className="mb-4" />
        )}

        {/* Success Message */}
        {success && (
          <SuccessMessage message={success} className="mb-4" />
        )}

        {/* Update Error */}
        {updateNotificationsMutation.error && (
          <ErrorMessage error={updateNotificationsMutation.error} className="mb-4" />
        )}

        {/* Settings */}
        {!isLoading && (
          <div className="space-y-6">
            {/* Info Box */}
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-start space-x-3">
                <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">À propos des notifications par email</p>
                  <p>
                    Lorsque cette option est activée, vous recevrez un email chaque fois qu'une nouvelle alerte 
                    de sanctions est détectée pour les entités surveillées dans vos organisations. 
                    Cela vous permet de réagir rapidement aux changements importants.
                  </p>
                </div>
              </div>
            </div>

            {/* Email Alerts Toggle */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-start space-x-3">
                <Mail className="h-5 w-5 text-gray-600 mt-1 flex-shrink-0" />
                <div>
                  <p className="font-medium text-gray-900">Alertes par Email</p>
                  <p className="text-sm text-gray-600 mt-1">
                    Recevoir un email lorsqu'une nouvelle alerte est détectée
                  </p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={emailAlertsEnabled}
                  onChange={(e) => handleToggle(e.target.checked)}
                  disabled={isSaving}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 peer-disabled:opacity-50"></div>
              </label>
            </div>

            {/* Save Button */}
            {hasChanges && (
              <div className="flex justify-end pt-4 border-t border-gray-200">
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSaving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Enregistrement...</span>
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      <span>Sauvegarder les modifications</span>
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationSettings;

